import React, {useRef, useState} from 'react';
import {
	Button,
	ButtonDropdownField,
	DropdownList,
	FlexColumn,
	FlexContainer,
	FlexRow,
	Icon,
	Label,
	Modal,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Util from '../../../forecast-app/shared/util/util';
import UpdateClientMutation from '../../../mutations/update_client_mutation';
import {getDefaultMediumClientLogo} from '../../../components/default_avatars';
import {BUTTON_COLOR, BUTTON_STYLE, isoCountries} from '../../../constants';
import {createFragmentContainer, graphql} from 'react-relay';
import Warning from '../../../components/warning';
import type {EditClientDetailsModal_viewer$data as EditClientDetailsModal_viewer} from './__generated__/EditClientDetailsModal_viewer.graphql';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {clientLogoSrc} from '../../../directApi';

type IsoCountryType = {
	value: string;
	label: string;
};

type EditClientDetailsModalProps = {
	clientId: number;
	closeModal: () => void;
	viewer: EditClientDetailsModal_viewer;
};

enum LogoState {
	NO_CHANGE = 'NO_CHANGE',
	UPDATE = 'UPDATE',
	DELETE = 'DELETE',
}

const EditClientDetailsModal = ({clientId, viewer, closeModal}: EditClientDetailsModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {client} = viewer?.company || {};
	const changeAvatarButtonRef = useRef<HTMLInputElement>(null);

	const [name, setName] = useState<string>(client?.name || '');
	const [street, setStreet] = useState<string>(client?.street || '');
	const [postcode, setPostcode] = useState<string>(client?.zip || '');
	const [city, setCity] = useState<string>(client?.city || '');
	const [vat, setVat] = useState<string>(client?.vat || '');
	const [country, setCountry] = useState<IsoCountryType | null>(
		isoCountries.find((_country: IsoCountryType) => _country.value === client?.country) || null
	);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [logoState, setLogoState] = useState<LogoState>(LogoState.NO_CHANGE);
	const [logo, setLogo] = useState<string>(
		client?.logoId ? clientLogoSrc(client?.logoId) : getDefaultMediumClientLogo(client?.logoDefaultId)
	);

	const onClickChangeAvatarButton = () => {
		changeAvatarButtonRef.current?.click();
	};

	const onChangeAvatar = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];

		if (!file) return;

		Util.confirmFilePreviewType(file, res => {
			if (res) {
				setLogoState(LogoState.UPDATE);
				setSelectedFile(file);
				setLogo(URL.createObjectURL(file));
			} else {
				closeModal();
				showModal({
					type: MODAL_TYPE.GENERIC,
					content: (
						<div className="default-warning-modal">
							<Warning messageId="warning_modal.profile_picture_wrong_format_title" />
							<div className="warning-part-2">
								{formatMessage({id: 'warning_modal.profile_picture_wrong_format_info'})}
							</div>
						</div>
					),
					buttons: [
						{
							text: formatMessage({id: 'common.cancel'}),
							style: BUTTON_STYLE.FILLED,
							color: BUTTON_COLOR.WHITE,
						},
					],
				});
			}
		});
	};

	const onClickRemoveAvatar = () => {
		setLogoState(LogoState.DELETE);
		setSelectedFile(null);
		setLogo(getDefaultMediumClientLogo(client?.logoDefaultId));
	};

	const onClickConfirmChanges = () => {
		const mutationBody = {
			id: clientId,
			name,
			street,
			zip: postcode,
			city,
			vat,
			country: country?.value || '',
			...(logoState === LogoState.DELETE ? {logoId: 'delete'} : {}),
		};

		if (logoState === LogoState.UPDATE) {
			Util.CommitMutation(UpdateClientMutation, mutationBody, closeModal, true, {
				id: clientId,
				file: selectedFile,
			});
		} else {
			Util.CommitMutation(UpdateClientMutation, mutationBody, closeModal);
		}
	};

	const onClickSelectCountry = (value: IsoCountryType) => {
		setCountry(value);
	};

	const isRemoveAvatarButtonDisabled = (!client?.logoId && logoState !== LogoState.UPDATE) || logoState === LogoState.DELETE;

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="common.edit_details" />
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<FlexRow gap={'l'}>
						<FlexContainer>
							<img crossOrigin="use-credentials" alt="client-logo" src={logo} width="96" height="96" />
						</FlexContainer>
						<FlexColumn>
							<input
								type="file"
								style={{display: 'none'}}
								onChange={onChangeAvatar.bind(this)}
								ref={changeAvatarButtonRef}
							/>
							<Button emphasis="medium" onPress={onClickChangeAvatarButton}>
								<FormattedMessage id="common.change_avatar" />
							</Button>
							<Button emphasis="medium" onPress={onClickRemoveAvatar} disabled={isRemoveAvatarButtonDisabled}>
								{isRemoveAvatarButtonDisabled ? (
									<FormattedMessage id="common.remove" />
								) : (
									<FormattedMessage id="common.remove_avatar" />
								)}
							</Button>
						</FlexColumn>
					</FlexRow>

					<TextField
						label={formatMessage({id: 'settings.client-contact-name'})}
						value={name}
						onValueChange={setName}
						size="large"
						width="flex"
					/>

					<TextField
						label={formatMessage({id: 'settings.client-company-street'})}
						value={street}
						onValueChange={setStreet}
						size="large"
						width="flex"
					/>

					<TextField
						label={formatMessage({id: 'settings.client-company-postcode-zip'})}
						value={postcode}
						onValueChange={setPostcode}
						size="large"
						width="flex"
					/>

					<TextField
						label={formatMessage({id: 'settings.client-company-city'})}
						value={city}
						onValueChange={setCity}
						size="large"
						width="flex"
					/>

					<TextField
						label={formatMessage({id: 'settings.client-company-vat'})}
						value={vat}
						onValueChange={setVat}
						size="large"
						width="flex"
					/>

					<FlexColumn gap={'s'}>
						<Label>Country</Label>
						<DropdownList>
							<DropdownList.Trigger>
								<ButtonDropdownField>
									<Text>{country?.label ?? ''}</Text>
								</ButtonDropdownField>
							</DropdownList.Trigger>
							<DropdownList.Content align={'start'}>
								{isoCountries.map(current => (
									<DropdownList.Item key={current.value} onClick={() => onClickSelectCountry(current)}>
										<FlexRow>
											<Text>{current.label}</Text>
											{current?.value === country?.value && <Icon icon={'iconTick'} />}
										</FlexRow>
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						</DropdownList>
					</FlexColumn>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={onClickConfirmChanges}>
					<FormattedMessage id={'common.confirm_changes'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const EditClientDetailsModalQuery = graphql`
	query EditClientDetailsModal_Query($clientId: String) {
		viewer {
			actualPersonId
			component(name: "edit_client_details_modal")
			...EditClientDetailsModal_viewer @arguments(clientId: $clientId)
		}
	}
`;

export default createFragmentContainer(EditClientDetailsModal, {
	viewer: graphql`
		fragment EditClientDetailsModal_viewer on Viewer @argumentDefinitions(clientId: {type: "String"}) {
			id
			availableFeatureFlags {
				key
			}
			company {
				id
				tier
				client(id: $clientId) {
					id
					name
					street
					zip
					city
					vat
					country
					notes
					logoId
					logoDefaultId
				}
			}
		}
	`,
});
